import moment from 'moment';
import { DEFAULT_LOCATION_LABEL, isLocationMessage, parseLocationMessage } from './locationUtils';

export const STATUS_ORDER = { pending: 0, sent: 1, delivered: 2, read: 3 };

const PRESET_TYPES = new Set(['li_preset', 'cr_preset', 'template']);

const MEDIA_MENU_TYPES = new Set(['video', 'image', 'audio', 'document', 'sticker']);

export const getChatMenuPreviewText = (chatItem) => {
  const lastMessage = chatItem?.lastMessage;
  if (!lastMessage) {
    return '';
  }

  const isFromMe = lastMessage.from === 'me';
  const type = String(
    lastMessage.msgType
    || (!isFromMe ? lastMessage.status?.messages_type : null)
    || 'text'
  ).toLowerCase();

  if (MEDIA_MENU_TYPES.has(type)) {
    return type.toUpperCase();
  }

  const previewSource = {
    ...lastMessage,
    text: lastMessage.text || (!isFromMe ? lastMessage.status?.text_body : '') || '',
    msgType: type,
    type,
  };

  if (type === 'location' || isLocationMessage(previewSource)) {
    return parseLocationMessage(previewSource)?.label || DEFAULT_LOCATION_LABEL;
  }

  if (lastMessage.text) {
    return lastMessage.text;
  }

  if (lastMessage.preset_name) {
    return lastMessage.preset_name;
  }

  if (!isFromMe && lastMessage.status?.text_body) {
    return lastMessage.status.text_body;
  }

  return '';
};

const formatMesgTime = (value) => {
  if (!value) {
    return value;
  }

  if (typeof value === 'string' && /^\d{1,2}:\d{2}\s[AP]M$/i.test(value)) {
    return value;
  }

  return moment(value).format('h:mm A');
};

export const normalizeConversationMessage = (message) => {
  if (!message || message.msgType === 'date' || message.msgType === 'system') {
    return message;
  }

  const from = message.from || 'me';
  const rawType = (
    from === 'me'
      ? message.type || message.msgType
      : message.msgType || message.type
  ) || 'text';
  let contentType = String(rawType).toLowerCase();
  if (isLocationMessage(message)) {
    contentType = 'location';
  }
  const presetName = message.preset_name || null;
  let text = PRESET_TYPES.has(contentType) && presetName
    ? presetName
    : message.text;

  if (contentType === 'location') {
    const parsed = parseLocationMessage(message);
    if (parsed?.label) {
      text = parsed.label;
    }
  }

  return {
    ...message,
    type: contentType,
    text,
    preset_name: presetName,
    msgType: contentType,
    keyword_blocked: Boolean(message.keyword_blocked),
    original_message: message.original_message ?? null,
    latitude: message.latitude ?? parseLocationMessage(message)?.latitude ?? null,
    longitude: message.longitude ?? parseLocationMessage(message)?.longitude ?? null,
    location_name: message.location_name ?? parseLocationMessage(message)?.name ?? null,
    location_address: message.location_address ?? parseLocationMessage(message)?.address ?? null,
    stored_message: message.stored_message ?? null,
    message_id: message.message_id ?? null,
    reply_to: message.reply_to ?? null,
    quoted_message_wamid: message.quoted_message_wamid ?? null,
    mesgTime: formatMesgTime(message.created_at || message.mesgTime),
    created_at: message.created_at || message.mesgTime,
  };
};

export const CSW_WINDOW_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_CHAT_FILTER = 'active';

export const applyCustomerIncomingCswState = (chat, messageTime) => {
  const lastAt = messageTime ? new Date(messageTime) : new Date();
  if (Number.isNaN(lastAt.getTime())) {
    return chat;
  }

  const expiresAt = new Date(lastAt.getTime() + CSW_WINDOW_MS);

  return {
    ...chat,
    is_csw_open: true,
    is_expired: false,
    csw_expires_at: expiresAt.toISOString(),
    last_customer_message_at: lastAt.toISOString(),
  };
};

const mergeCswStateFromPayload = (chat, messageData, lastMessage) => {
  if (messageData?.is_csw_open !== undefined || messageData?.is_expired !== undefined) {
    return {
      ...chat,
      is_csw_open: Boolean(messageData.is_csw_open),
      is_expired: Boolean(messageData.is_expired),
      csw_expires_at: messageData.csw_expires_at ?? null,
      last_customer_message_at: messageData.last_customer_message_at ?? null,
    };
  }

  const isChatbotTrigger =
    Boolean(messageData?.is_chatbot_trigger) ||
    Boolean(lastMessage?.is_chatbot_trigger);

  if (lastMessage?.from === 'opposite' && !isChatbotTrigger) {
    return applyCustomerIncomingCswState(
      chat,
      lastMessage.time || lastMessage.created_at
    );
  }

  return chat;
};

export const normalizeChatFilter = (filter) =>
  filter === 'expired' ? 'expired' : DEFAULT_CHAT_FILTER;

export const matchesChatFilter = (chat, filter) => {
  const normalizedFilter = normalizeChatFilter(filter);
  const expired = Boolean(chat?.is_expired);
  return normalizedFilter === 'expired' ? expired : !expired;
};

export const findChatByNumber = (chats, number) => {
  if (!number || !Array.isArray(chats)) {
    return null;
  }

  const key = String(number);
  return chats.find((chat) => String(chat?.number) === key) ?? null;
};

export const resolveCswStateFromChat = (chat) => {
  if (!chat) {
    return null;
  }

  return {
    is_expired: Boolean(chat.is_expired),
    csw_expires_at: chat.csw_expires_at ?? null,
  };
};

export const resolveCswFromMenuPayload = (messageData) => {
  if (!messageData) {
    return null;
  }

  if (messageData.is_csw_open !== undefined || messageData.is_expired !== undefined) {
    return {
      is_expired: Boolean(messageData.is_expired),
      csw_expires_at: messageData.csw_expires_at ?? null,
    };
  }

  const lastMessage = messageData.lastMessage;
  const isChatbotTrigger =
    Boolean(messageData.is_chatbot_trigger) ||
    Boolean(lastMessage?.is_chatbot_trigger);

  if (lastMessage?.from === 'opposite' && !isChatbotTrigger) {
    return resolveCswStateFromChat(
      applyCustomerIncomingCswState({}, lastMessage.time || lastMessage.created_at),
    );
  }

  return null;
};

export const resolveCswFromIncomingMessage = (message) => {
  if (!message) {
    return null;
  }

  if (message.is_csw_open !== undefined || message.is_expired !== undefined) {
    return {
      is_expired: Boolean(message.is_expired),
      csw_expires_at: message.csw_expires_at ?? null,
    };
  }

  if (message.from === 'opposite' && !message.is_chatbot_trigger) {
    return resolveCswStateFromChat(
      applyCustomerIncomingCswState({}, message.created_at || message.mesgTime),
    );
  }

  return null;
};

export const resolveCswStateFromPayload = (payload, chatList, contactId) => {
  const fromList = resolveCswStateFromChat(findChatByNumber(chatList, contactId));
  const fromPayload = payload
    ? {
        is_expired: Boolean(payload.is_expired),
        csw_expires_at: payload.csw_expires_at ?? null,
      }
    : null;

  if (!fromList) {
    return fromPayload;
  }

  if (!fromPayload) {
    return fromList;
  }

  const listAt = fromList.csw_expires_at
    ? new Date(fromList.csw_expires_at).getTime()
    : 0;
  const payloadAt = fromPayload.csw_expires_at
    ? new Date(fromPayload.csw_expires_at).getTime()
    : 0;

  if (listAt >= payloadAt) {
    return fromList;
  }

  return fromPayload;
};

/** When viewing one tab, returns the other tab if this chat belongs there. */
export const resolveCrossFilterTarget = (chat, currentFilter) => {
  if (!chat) {
    return null;
  }

  const viewing = normalizeChatFilter(currentFilter);
  if (viewing === 'expired' && matchesChatFilter(chat, 'active')) {
    return 'active';
  }
  if (viewing === 'active' && matchesChatFilter(chat, 'expired')) {
    return 'expired';
  }

  return null;
};

export const sortChatsByLatest = (chats = []) =>
  [...chats].sort((a, b) => {
    const timeA = new Date(a.lastMessage?.time || 0);
    const timeB = new Date(b.lastMessage?.time || 0);
    return timeB - timeA;
  });

export const applyCrossFilterSnapshots = (list = [], snapshots = [], filter) => {
  if (!snapshots.length) {
    return list;
  }

  const byNumber = new Map(list.map((chat) => [String(chat.number), chat]));

  snapshots.forEach((snap) => {
    if (!snap || !matchesChatFilter(snap, filter)) {
      return;
    }

    const key = String(snap.number);
    const existing = byNumber.get(key);

    if (!existing) {
      byNumber.set(key, snap);
      return;
    }

    if (
      snap.lastMessage
      && shouldReplaceLastMessage(existing.lastMessage, snap.lastMessage)
    ) {
      byNumber.set(key, {
        ...existing,
        ...snap,
        lastMessage: snap.lastMessage,
        unread: Math.max(existing.unread || 0, snap.unread || 0),
      });
    }
  });

  return sortChatsByLatest(Array.from(byNumber.values()));
};

export const lastMessageTimestamp = (lastMessage) => {
  const raw = lastMessage?.time || lastMessage?.created_at || lastMessage?.mesgTime || 0;
  return new Date(raw).getTime();
};

export const shouldReplaceLastMessage = (current, incoming) => {
  if (!current) {
    return true;
  }
  if (!incoming) {
    return false;
  }

  const currentTime = lastMessageTimestamp(current);
  const incomingTime = lastMessageTimestamp(incoming);

  if (incomingTime > currentTime) {
    return true;
  }

  if (incomingTime < currentTime) {
    return false;
  }

  if (incoming.from === 'me' && current.from !== 'me') {
    return true;
  }

  return (
    incoming.id !== current.id ||
    incoming.text !== current.text ||
    incoming.status !== current.status
  );
};

export const mergeChatLists = (prevList = [], nextList = []) => {
  const prevByNumber = new Map(
    prevList.map((chat) => [String(chat.number), chat])
  );

  return nextList.map((chat) => {
    const prev = prevByNumber.get(String(chat.number));
    if (!prev?.lastMessage || !chat?.lastMessage) {
      return chat;
    }

    if (shouldReplaceLastMessage(prev.lastMessage, chat.lastMessage)) {
      return chat;
    }

    return {
      ...chat,
      lastMessage: prev.lastMessage,
      unread: Math.max(prev.unread || 0, chat.unread || 0),
    };
  });
};

export const upsertChatInList = (prevList, messageData, options = {}) => {
  if (!messageData?.number || !messageData?.lastMessage) {
    return prevList;
  }

  const { isActiveChat = false } = options;
  const chatId = parseInt(messageData.number, 10);
  let found = false;
  let updatedChat = null;

  const filteredList = prevList.filter((chat) => {
    if (parseInt(chat.number, 10) === chatId) {
      found = true;
      const currentLastMsg = chat.lastMessage;
      const newLastMsg = messageData.lastMessage;

      if (!shouldReplaceLastMessage(currentLastMsg, newLastMsg)) {
        updatedChat = mergeCswStateFromPayload(
          isActiveChat ? { ...chat, unread: 0 } : chat,
          messageData,
          newLastMsg
        );
        return false;
      }

      const isChatbotTrigger =
        Boolean(messageData.is_chatbot_trigger) ||
        Boolean(newLastMsg.is_chatbot_trigger);

      updatedChat = mergeCswStateFromPayload(
        {
          ...chat,
          name: messageData.name || chat.name,
          lastMessage: newLastMsg,
          unread: isActiveChat
            ? 0
            : newLastMsg.from !== 'me' && !isChatbotTrigger
              ? (chat.unread || 0) + 1
              : chat.unread || 0,
        },
        messageData,
        newLastMsg
      );
      return false;
    }
    return true;
  });

  if (found) {
    return [updatedChat, ...filteredList];
  }

  const isChatbotTrigger =
    Boolean(messageData.is_chatbot_trigger) ||
    Boolean(messageData.lastMessage?.is_chatbot_trigger);

  return [
    mergeCswStateFromPayload(
      {
        number: messageData.number,
        name: messageData.name || 'Unknown',
        lastMessage: messageData.lastMessage,
        unread: isActiveChat
          ? 0
          : messageData.lastMessage.from !== 'me' && !isChatbotTrigger
            ? 1
            : 0,
      },
      messageData,
      messageData.lastMessage
    ),
    ...filteredList,
  ];
};

const looksLikeRawLocationJson = (text) => {
  const raw = String(text || "").trim();
  return raw.startsWith("{") && /latitude|longitude|loc_name|location_name/i.test(raw);
};

const resolveCompleteLocationPayload = (...candidates) => {
  for (const candidate of candidates) {
    const raw = String(candidate || "").trim();
    if (!looksLikeRawLocationJson(raw)) {
      continue;
    }

    try {
      JSON.parse(raw);
      return raw;
    } catch {
      // Ignore truncated/invalid JSON fragments from menu preview text.
    }
  }

  return null;
};

const resolveLocationDisplayText = (...candidates) => {
  for (const candidate of candidates) {
    const raw = String(candidate || "").trim();
    if (!raw || looksLikeRawLocationJson(raw)) {
      continue;
    }
    return raw;
  }

  return "";
};

const messagesAreSame = (left, right) =>
  (left?.id && right?.id && String(left.id) === String(right.id)) ||
  (left?.message_id &&
    right?.message_id &&
    String(left.message_id) === String(right.message_id)) ||
  (left?.message_id && right?.id && String(left.message_id) === String(right.id)) ||
  (right?.message_id && left?.id && String(right.message_id) === String(left.id));

const resolveConversationText = (existingText, incomingText) => {
  const current = String(existingText || '').trim();
  const next = String(incomingText || '').trim();

  if (!current) {
    return next;
  }

  if (!next || current === next) {
    return current;
  }

  // Sidebar/menu socket previews are truncated — never replace full bubble text with shorter preview.
  if (current.length > next.length) {
    const nextWithoutEllipsis = next.replace(/\.\.\.$/, '');
    if (current.startsWith(nextWithoutEllipsis)) {
      return current;
    }
  }

  return next.length > current.length ? next : current;
};

const mergeConversationMessage = (existing, incoming) => {
  const merged = {
    ...existing,
    ...incoming,
    _tempId: existing._tempId || incoming._tempId || undefined,
    id: incoming.id ?? existing.id,
    message_id:
      incoming.message_id ||
      existing.message_id ||
      incoming.status_id ||
      existing.status_id ||
      null,
    status: incoming.status ?? existing.status,
  };

  const contentType = String(
    merged.msgType || merged.type || incoming.msgType || incoming.type || 'text'
  ).toLowerCase();

  if (
    contentType === 'location'
    || isLocationMessage(existing)
    || isLocationMessage(incoming)
    || isLocationMessage(merged)
  ) {
    const storedMessage = resolveCompleteLocationPayload(
      incoming.stored_message,
      existing.stored_message,
      incoming.text,
      existing.text,
    );

    const parsed = parseLocationMessage({
      ...merged,
      stored_message: storedMessage,
      text: resolveLocationDisplayText(existing.text, incoming.text, merged.text),
      latitude: merged.latitude ?? existing.latitude ?? incoming.latitude,
      longitude: merged.longitude ?? existing.longitude ?? incoming.longitude,
      location_name: merged.location_name ?? existing.location_name ?? incoming.location_name,
      location_address: merged.location_address ?? existing.location_address ?? incoming.location_address,
    });

    merged.type = 'location';
    merged.msgType = 'location';
    if (storedMessage) {
      merged.stored_message = storedMessage;
    } else if (existing.stored_message) {
      merged.stored_message = existing.stored_message;
    }

    if (parsed?.label) {
      merged.text = parsed.label;
    } else {
      const fallbackText = resolveLocationDisplayText(existing.text, incoming.text);
      if (fallbackText) {
        merged.text = fallbackText;
      }
    }

    merged.latitude = parsed?.latitude ?? merged.latitude ?? existing.latitude ?? null;
    merged.longitude = parsed?.longitude ?? merged.longitude ?? existing.longitude ?? null;
    merged.location_name = merged.location_name ?? parsed?.name ?? existing.location_name ?? null;
    merged.location_address = merged.location_address ?? parsed?.address ?? existing.location_address ?? null;
  } else {
    if (incoming.latitude == null && existing.latitude != null) {
      merged.latitude = existing.latitude;
    }
    if (incoming.longitude == null && existing.longitude != null) {
      merged.longitude = existing.longitude;
    }
    if (!incoming.location_name && existing.location_name) {
      merged.location_name = existing.location_name;
    }
    if (!incoming.location_address && existing.location_address) {
      merged.location_address = existing.location_address;
    }
  }

  if (incoming.reply_to) {
    merged.reply_to = incoming.reply_to;
  } else if (existing.reply_to) {
    merged.reply_to = existing.reply_to;
  }

  if (incoming.quoted_message_wamid) {
    merged.quoted_message_wamid = incoming.quoted_message_wamid;
  } else if (existing.quoted_message_wamid) {
    merged.quoted_message_wamid = existing.quoted_message_wamid;
  }

  if (incoming.preset_name) {
    merged.preset_name = incoming.preset_name;
  } else if (existing.preset_name) {
    merged.preset_name = existing.preset_name;
  }

  if (incoming.body_values) {
    merged.body_values = incoming.body_values;
  } else if (existing.body_values) {
    merged.body_values = existing.body_values;
  }

  if (incoming.original_message) {
    merged.original_message = incoming.original_message;
  } else if (existing.original_message) {
    merged.original_message = existing.original_message;
  }

  if (incoming.keyword_blocked || existing.keyword_blocked) {
    merged.keyword_blocked = true;
    if (incoming.text) {
      merged.text = incoming.text;
    } else if (existing.text) {
      merged.text = existing.text;
    }
  } else {
    merged.text = resolveConversationText(existing.text, merged.text);
  }

  if (PRESET_TYPES.has(contentType) && merged.preset_name) {
    merged.text = merged.preset_name;
  }

  return merged;
};

const matchesStatusMessage = (msg, statusMessage) => messagesAreSame(msg, statusMessage);

export const applyIncomingMessageMeta = (messages, metaMessage) => {
  if (!metaMessage?.id) {
    return messages;
  }

  const metaId = String(metaMessage.id);
  const hasMatch = messages.some((msg) => String(msg.id) === metaId);
  if (!hasMatch) {
    return messages;
  }

  return messages.map((msg) =>
    String(msg.id) === metaId ? { ...msg, ...metaMessage } : msg
  );
};

export const applyMessageStatus = (messages, statusMessage) => {
  if (!statusMessage?.id && !statusMessage?.message_id) {
    return messages;
  }

  const existing = messages.find((item) => matchesStatusMessage(item, statusMessage));
  if (!existing) {
    return [...messages, { ...statusMessage, statusUpdatedViaSocket: true }];
  }

  const nextMessages = [];
  let merged = false;

  messages.forEach((msg) => {
    if (!matchesStatusMessage(msg, statusMessage)) {
      nextMessages.push(msg);
      return;
    }

    if (merged) {
      return;
    }

    const currentStatus = msg.status;
    const newStatus = statusMessage.status;
    if (
      STATUS_ORDER[newStatus] !== undefined &&
      STATUS_ORDER[currentStatus] !== undefined &&
      STATUS_ORDER[newStatus] < STATUS_ORDER[currentStatus]
    ) {
      nextMessages.push({ ...msg, statusUpdatedViaSocket: true });
    } else {
      nextMessages.push({
        ...mergeConversationMessage(msg, statusMessage),
        statusUpdatedViaSocket: true,
      });
    }

    merged = true;
  });

  return nextMessages;
};

export const appendUniqueMessage = (messages, message) => {
  if (!message?.id && !message?.message_id) {
    return [...messages, message];
  }

  const duplicateIndex = messages.findIndex((item) => messagesAreSame(item, message));
  if (duplicateIndex === -1) {
    return [...messages, message];
  }

  return messages.map((item, index) =>
    index === duplicateIndex ? mergeConversationMessage(item, message) : item
  );
};

export const menuLastMessageToConversationMessage = (lastMessage, waId) => {
  if (!lastMessage) {
    return null;
  }

  if (lastMessage.from === 'opposite') {
    const contentType = isLocationMessage(lastMessage)
      ? 'location'
      : String(lastMessage.type || lastMessage.msgType || 'text').toLowerCase();
    const parsedLocation = contentType === 'location'
      ? parseLocationMessage(lastMessage)
      : null;
    const isPreset = PRESET_TYPES.has(contentType);

    return normalizeConversationMessage({
      type: contentType,
      text: isPreset
        ? (lastMessage.preset_name || lastMessage.text)
        : (parsedLocation?.label || lastMessage.text),
      preset_name: lastMessage.preset_name || null,
      body_values: lastMessage.body_values || null,
      from: 'opposite',
      msgType: contentType,
      media_id: lastMessage.media_id,
      media_url: lastMessage.media_url,
      mime_type: lastMessage.mime_type,
      file_name: lastMessage.file_name,
      caption: lastMessage.caption,
      latitude: lastMessage.latitude ?? parsedLocation?.latitude ?? null,
      longitude: lastMessage.longitude ?? parsedLocation?.longitude ?? null,
      location_name: lastMessage.location_name ?? parsedLocation?.name ?? null,
      location_address: lastMessage.location_address ?? parsedLocation?.address ?? null,
      reply_to: lastMessage.reply_to ?? null,
      quoted_message_wamid: lastMessage.quoted_message_wamid ?? null,
      id: lastMessage.id,
      wa_id: waId,
      mesgTime: lastMessage.time,
      created_at: lastMessage.time,
      is_chatbot_message: Boolean(lastMessage.is_chatbot_message),
    });
  }

  const contentType = isLocationMessage(lastMessage)
    ? 'location'
    : String(lastMessage.type || lastMessage.msgType || 'text').toLowerCase();
  const parsedLocation = contentType === 'location'
    ? parseLocationMessage(lastMessage)
    : null;
  const isMedia = ['image', 'video', 'document', 'audio'].includes(contentType);
  const isPreset = PRESET_TYPES.has(contentType);
  const messageText = contentType === 'location'
    ? (parsedLocation?.label || lastMessage.conversation_text || lastMessage.text)
    : isMedia
      ? (lastMessage.media_url || lastMessage.text)
      : isPreset
        ? (lastMessage.preset_name || lastMessage.text)
        : (lastMessage.conversation_text || lastMessage.preset_name || lastMessage.text);

  return normalizeConversationMessage({
    type: contentType,
    text: messageText,
    preset_name: lastMessage.preset_name || null,
    body_values: lastMessage.body_values || null,
    from: 'me',
    msgType: contentType,
    status: lastMessage.status,
    media_url: lastMessage.media_url || null,
    latitude: lastMessage.latitude ?? parsedLocation?.latitude ?? null,
    longitude: lastMessage.longitude ?? parsedLocation?.longitude ?? null,
    location_name: lastMessage.location_name ?? parsedLocation?.name ?? null,
    location_address: lastMessage.location_address ?? parsedLocation?.address ?? null,
    stored_message: resolveCompleteLocationPayload(
      lastMessage.stored_message,
      contentType === 'location' ? lastMessage.text : null,
    ),
    reply_to: lastMessage.reply_to ?? null,
    quoted_message_wamid: lastMessage.quoted_message_wamid ?? null,
    id: lastMessage.id,
    message_id: lastMessage.message_id || lastMessage.status_id || null,
    wa_id: waId,
    mesgTime: lastMessage.time,
    created_at: lastMessage.time,
  });
};
